import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";

function normalizePagedResponse(data) {
  if (Array.isArray(data)) {
    return { items: data, count: data.length };
  }
  const items = Array.isArray(data?.results) ? data.results : [];
  const count = typeof data?.count === "number" ? data.count : items.length;
  return { items, count };
}

export const fetchStores = createAsyncThunk(
  "stores/fetchStores",
  async ({ search = "", page = 1, pageSize = 50 } = {}) => {
    const qp = new URLSearchParams();
    if (String(search || "").trim()) qp.set("search", String(search || "").trim());
    qp.set("ordering", "name");
    qp.set("page", String(page || 1));
    qp.set("page_size", String(pageSize || 50));
    const path = `/api/product/stores/${qp.toString() ? `?${qp.toString()}` : ""}`;
    const data = await apiFetch(path, { method: "GET" });
    return normalizePagedResponse(data);
  }
);

export const fetchStoreDetail = createAsyncThunk("stores/fetchStoreDetail", async ({ storeId }) => {
  return await apiFetch(`/api/product/stores/${storeId}/`, { method: "GET" });
});

export const updateStore = createAsyncThunk(
  "stores/updateStore",
  async ({ storeId, data }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/product/stores/${storeId}/`, {
        method: "PATCH",
        body: data,
      });
    } catch (err) {
      return rejectWithValue(err?.message || "Ошибка сохранения");
    }
  }
);

/** Director / manager: today's open shift per store. */
export const fetchStoreShiftOpenStatus = createAsyncThunk(
  "stores/fetchStoreShiftOpenStatus",
  async () => apiFetch("/api/product/stores/shift-open-status/", { method: "GET" })
);

const slice = createSlice({
  name: "stores",
  initialState: {
    list: {
      items: [],
      count: 0,
      status: "idle",
      error: null,
      lastSearch: "",
      lastPage: 1,
      lastPageSize: 50,
    },
    shiftOpenStatus: {
      date: null,
      stores: [],
      status: "idle",
      error: null,
    },
    byId: {},
    statusById: {},
    errorById: {},
    updateStatusById: {},
    updateErrorById: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStores.pending, (state, action) => {
        state.list.status = "loading";
        state.list.error = null;
        state.list.lastSearch = action.meta.arg?.search || "";
        state.list.lastPage = action.meta.arg?.page || 1;
        state.list.lastPageSize = action.meta.arg?.pageSize || 50;
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.list.status = "succeeded";
        state.list.items = action.payload?.items || [];
        state.list.count = action.payload?.count ?? state.list.items.length;
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.list.status = "failed";
        state.list.error = action.error?.message || "Failed to load stores";
      })
      .addCase(fetchStoreDetail.pending, (state, action) => {
        const id = String(action.meta.arg.storeId);
        state.statusById[id] = "loading";
        state.errorById[id] = null;
      })
      .addCase(fetchStoreDetail.fulfilled, (state, action) => {
        const id = String(action.payload?.id ?? action.meta.arg.storeId);
        state.statusById[id] = "succeeded";
        state.byId[id] = action.payload;
      })
      .addCase(fetchStoreDetail.rejected, (state, action) => {
        const id = String(action.meta.arg.storeId);
        state.statusById[id] = "failed";
        state.errorById[id] = action.error?.message || "Failed to load store";
      })
      .addCase(updateStore.pending, (state, action) => {
        const id = String(action.meta.arg.storeId);
        state.updateStatusById[id] = "loading";
        state.updateErrorById[id] = null;
      })
      .addCase(updateStore.fulfilled, (state, action) => {
        const id = String(action.payload?.id ?? action.meta.arg.storeId);
        state.updateStatusById[id] = "succeeded";
        state.updateErrorById[id] = null;
        state.byId[id] = action.payload;
      })
      .addCase(updateStore.rejected, (state, action) => {
        const id = String(action.meta.arg.storeId);
        state.updateStatusById[id] = "failed";
        state.updateErrorById[id] = action.payload || action.error?.message || "Ошибка";
      })
      .addCase(fetchStoreShiftOpenStatus.pending, (state) => {
        state.shiftOpenStatus.status = "loading";
        state.shiftOpenStatus.error = null;
      })
      .addCase(fetchStoreShiftOpenStatus.fulfilled, (state, action) => {
        state.shiftOpenStatus.status = "succeeded";
        state.shiftOpenStatus.date = action.payload?.date ?? null;
        state.shiftOpenStatus.stores = Array.isArray(action.payload?.stores) ? action.payload.stores : [];
      })
      .addCase(fetchStoreShiftOpenStatus.rejected, (state, action) => {
        state.shiftOpenStatus.status = "failed";
        state.shiftOpenStatus.error =
          action.error?.message || String(action.payload || "Failed to load shift status");
      });
  },
});

export default slice.reducer;

export const selectStores = (state) => state.stores.list.items || [];
export const selectStoresCount = (state) => Number(state.stores.list.count || 0);
export const selectStoresStatus = (state) => state.stores.list.status || "idle";
export const selectStoresError = (state) => state.stores.list.error || null;

export const selectStoreById = (id) => (state) => state.stores.byId[String(id)] || null;
export const selectStoreStatusById = (id) => (state) => state.stores.statusById[String(id)] || "idle";
export const selectStoreErrorById = (id) => (state) => state.stores.errorById[String(id)] || null;

export const selectStoreUpdateStatus = (id) => (state) => state.stores.updateStatusById[String(id)] || "idle";
export const selectStoreUpdateError = (id) => (state) => state.stores.updateErrorById[String(id)] || null;

export const selectShiftOpenStatusDate = (state) => state.stores.shiftOpenStatus?.date || null;
export const selectShiftOpenStatusRows = (state) => state.stores.shiftOpenStatus?.stores || [];
export const selectShiftOpenStatusLoadStatus = (state) => state.stores.shiftOpenStatus?.status || "idle";
export const selectShiftOpenStatusError = (state) => state.stores.shiftOpenStatus?.error || null;
